import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface BreadcrumbItem {
  label: ReactNode;
  /** Omit on the current page. */
  to?: string;
}

export function Breadcrumbs({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-sm">
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={i} className="flex items-center gap-1">
              {item.to && !last ? (
                <RouterLink to={item.to} className="inline-flex min-h-11 min-w-11 items-center justify-center font-bold text-primary underline underline-offset-4">
                  {item.label}
                </RouterLink>
              ) : (
                <span aria-current={last ? 'page' : undefined} className="text-muted">
                  {item.label}
                </span>
              )}
              {!last && <ChevronRight aria-hidden="true" className="h-4 w-4 text-muted" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface PageHeaderProps {
  /** Page title — the page's single h1. */
  title: ReactNode;
  description?: ReactNode;
  /** Primary actions (e.g. "Add gift"). */
  actions?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Back link shown on small screens instead of breadcrumbs. */
  back?: { to: string; label: string };
  /** Extra content under the title, e.g. badges or an <InfoButton>. */
  children?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, actions, breadcrumbs, back, children, className }: PageHeaderProps) {
  return (
    <header className={cn('mb-6 flex flex-col gap-2', className)}>
      {back && (
        <RouterLink
          to={back.to}
          className={cn(
            'inline-flex min-h-11 items-center gap-1 self-start font-bold text-primary underline underline-offset-4',
            breadcrumbs && 'lg:hidden',
          )}
        >
          <ChevronLeft aria-hidden="true" className="h-5 w-5" />
          {back.label}
        </RouterLink>
      )}
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} className={cn(back && 'hidden lg:block')} />}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0 sm:min-w-[16rem] sm:flex-1">
          <h1 className="text-2xl sm:text-3xl" tabIndex={-1} data-page-heading="">
            {title}
          </h1>
          {description && <p className="mt-1 max-w-prose text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
  /** What is being paged, for the summary ("gifts"). */
  itemLabel?: string;
  className?: string;
}

/** Offset pagination matching the API's `{ items, total, limit, offset }` envelope. */
export function Pagination({ total, limit, offset, onChange, itemLabel = 'items', className }: PaginationProps) {
  if (total <= limit) return null;
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  const from = Math.min(total, offset + 1);
  const to = Math.min(total, offset + limit);
  const btn =
    'inline-flex min-h-11 items-center gap-1 rounded-lg border-2 border-primary px-3 font-bold text-primary hover:bg-primary-soft disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <nav aria-label="Pagination" className={cn('mt-4 flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="text-muted" aria-live="polite">
        Showing {from}–{to} of {total} {itemLabel} (page {page} of {pages})
      </p>
      <div className="flex gap-2">
        <button type="button" className={btn} disabled={page <= 1} onClick={() => onChange(Math.max(0, offset - limit))}>
          <ChevronLeft aria-hidden="true" className="h-5 w-5" />
          Previous<span className="sr-only"> page</span>
        </button>
        <button type="button" className={btn} disabled={page >= pages} onClick={() => onChange(offset + limit)}>
          Next<span className="sr-only"> page</span>
          <ChevronRight aria-hidden="true" className="h-5 w-5" />
        </button>
      </div>
    </nav>
  );
}
